// fonts.ts — the shared WI web-font family stacks for the in-game DOM UI shell
// (HTML/DOM UI shell plan, Phase 5 font pass). The DOM forms now render in the
// game's ACTUAL source typefaces (the originals the bitmap UI fonts were drawn
// from), self-hosted as woff2 under /fonts/ and declared via @font-face in the
// shell's index.html (scripts/build-ts-client.mjs):
//
//   WITitle      = SteelWolf.ttf        — the TITLE face (kifntTitle / titlefont):
//                  form headings AND the gob NAME labels above units (the gob
//                  context-menu title strip). A sleek, angular, cut-corner display
//                  face. (CONFIRMED by glyph montage — the titlefont bitmap digits
//                  match SteelWolf, NOT Quadtrangle as first assumed; Quadtrangle is
//                  the WARFARE wordmark logo art, not a runtime font.)
//   WIEurostile  = Eurostyle Extended.TTF (Eurostile Extended) — the BUTTON face
//                  (kifntButton: button labels) AND the BODY/STANDARD/SHADOW/HUD
//                  faces (kifntStandard/Default/Ecom/Shadow/Hud: stat labels/values,
//                  descriptions, the gob menu body, the HUD readout). Confirmed by
//                  montage that those WI bitmap fonts all derive from Eurostile —
//                  same family, different bitmap sizes (shadow adds a drop shadow).
//
// Each stack keeps a sensible sans-serif fallback so the layout is sane while the
// woff2 streams in (@font-face is font-display:swap; index.html preloads both).
//
// The two FALLBACK families (no WI face) match the prior shell look so a pack /
// offline boot without the fonts still reads cleanly.
const FALLBACK = 'ui-sans-serif, "Segoe UI", system-ui, sans-serif';

/** TITLE / form-heading + unit-label face (SteelWolf). */
export const FONT_TITLE = `'WITitle', ${FALLBACK}`;
/** BUTTON-label face (Eurostile Extended). */
export const FONT_BUTTON = `'WIEurostile', ${FALLBACK}`;
/** BODY / label / value / description face (Eurostile Extended). */
export const FONT_BODY = `'WIEurostile', ${FALLBACK}`;

/** the woff2 the shell ships (index.html @font-face src), for warm-up. */
const WI_FONTS: ReadonlyArray<{ family: string; weight: string }> = [
  { family: 'WITitle', weight: 'normal' },
  { family: 'WIEurostile', weight: 'normal' },
];

let _measureCtx: CanvasRenderingContext2D | null = null;
/**
 * Measure `text`'s rendered width in px at the given pixel size + family (the WI
 * button face, once warmed). Used to size buttons to their label so the wide WI
 * faces never collide with the 9-slice frame border. Rough char estimate where
 * canvas 2d is unavailable (node/SSR).
 */
export function measureTextPx(text: string, px: number, family: string): number {
  if (_measureCtx === null) _measureCtx = document.createElement('canvas').getContext('2d');
  if (_measureCtx === null) return text.length * px * 0.62;
  _measureCtx.font = `600 ${px}px ${family}`;
  return _measureCtx.measureText(text).width;
}

/** design-unit breathing room a 9-slice button adds around its label = the frame
 *  CAPS (each rounded end is ≈0.45×the button height, so both ≈0.9h) + a margin so
 *  the WI face clears the border. Caps scale with the button HEIGHT, so the default
 *  pad is height-relative; a wider explicit pad can be passed per row (a tight form). */
export const BUTTON_PAD_U = 9;

/**
 * The design-unit WIDTH a 9-slice button needs to hold `label` in the WI button
 * face at row height `hU` (design units), given `s` px per design unit. =
 * the measured label width (INCLUDING the 0.05em letter-spacing the button renders) +
 * a pad that clears the 9-slice CAPS + a margin. EVERY DOM form's button row uses this
 * so buttons size to their text — never hand-tuned fixed widths that collide with the
 * frame. `padU` defaults to caps≈0.9h + a 5u margin (the cap proportion the lobby row
 * was tuned to at h=12 ≈ 16u); pass an explicit value only to override.
 */
export function buttonWidthU(
  label: string,
  hU: number,
  s: number,
  minW = 36,
  padU = Math.round(hU * 0.9) + 5,
): number {
  const fontPx = Math.max(9, Math.round(hU * s * 0.5)); // button WIDTH stays sized at 0.5×h; the label RENDERS at 0.46×h (menu-view/chrome) so the text drops ~1pt WITHOUT shrinking the button
  // the rendered button adds letterSpacing:0.05em ((n-1) gaps) that measureText omits.
  const textPx =
    measureTextPx(label, fontPx, FONT_BUTTON) + Math.max(0, label.length - 1) * fontPx * 0.05;
  return Math.max(minW, Math.ceil(textPx / s + padU));
}

let warmed = false;

/**
 * Kick the browser to fetch + decode the WI web fonts ONCE at boot, so the
 * in-game DOM views lay out (measure text) in the real face instead of the
 * fallback and then reflowing. Fire-and-forget — @font-face's font-display:swap
 * means a view that mounts before the font arrives just paints the fallback and
 * swaps in; this warm-up just minimizes that window. No-op where the FontFaceSet
 * API is missing (older engines) — the @font-face still applies on paint.
 */
export function warmWiFonts(): void {
  if (warmed) return;
  warmed = true;
  const fonts = (globalThis as { document?: { fonts?: FontFaceSet } }).document?.fonts;
  if (fonts === undefined || typeof fonts.load !== 'function') return;
  for (const f of WI_FONTS) {
    // a representative size; the FontFaceSet loads the whole face regardless.
    void fonts.load(`${f.weight} 16px ${f.family}`).catch(() => {
      /* fallback stays */
    });
  }
}
